import React, { memo } from 'react';
import { Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { ChatMessage } from './types';

interface ChatBubbleProps {
  message: ChatMessage;
}

const ChatBubble: React.FC<ChatBubbleProps> = memo(({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-2.5', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="h-3.5 w-3.5 text-primary" />
        </div>
      )}
      <div className={cn(
        'max-w-[82%] rounded-2xl text-[13px] leading-relaxed',
        isUser
          ? 'bg-primary text-primary-foreground px-3.5 py-2.5 rounded-br-md'
          : 'bg-muted text-foreground px-3.5 py-2.5 rounded-bl-md',
        message.isError && 'border border-destructive/30 bg-destructive/10 text-destructive'
      )}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none
            [&>p]:mb-1.5 [&>p:last-child]:mb-0
            [&>ul]:mb-1.5 [&>ul]:pl-4 [&>ol]:mb-1.5 [&>ol]:pl-4
            [&>li]:mb-0.5
            [&>h1]:text-sm [&>h2]:text-sm [&>h3]:text-[13px] [&>h4]:text-[13px]
            [&>h1]:mt-2 [&>h2]:mt-2 [&>h3]:mt-1.5 [&>h4]:mt-1
            [&>h1]:mb-1 [&>h2]:mb-1 [&>h3]:mb-0.5
            [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-medium
            [&>blockquote]:border-l-2 [&>blockquote]:border-primary/30 [&>blockquote]:pl-3 [&>blockquote]:italic [&>blockquote]:text-muted-foreground
            [&>pre]:bg-background/50 [&>pre]:rounded-lg [&>pre]:p-2 [&>pre]:text-xs
            [&_code]:text-xs [&_code]:bg-background/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded
            [&_strong]:font-semibold [&_strong]:text-foreground
          ">
            <ReactMarkdown>{message.content || '...'}</ReactMarkdown>
          </div>
        )}
        <p className={cn(
          'text-[10px] mt-1.5 select-none',
          isUser ? 'text-primary-foreground/60' : 'text-muted-foreground/70'
        )}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-primary-foreground text-[10px] font-bold">U</span>
        </div>
      )}
    </div>
  );
});

ChatBubble.displayName = 'ChatBubble';

export default ChatBubble;
